import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/layout/Layout';
import InstagramFeed from '@/components/InstagramFeed';
import Testimonials from '@/components/Testimonials';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Users, 
  Sparkles, 
  Shield, 
  Clock, 
  Star, 
  Globe,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Instagram,
  ExternalLink,
  Building2,
  Calendar,
  Trophy,
  Plane,
  Tag
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_HERO_IMAGE = "https://images.unsplash.com/photo-1506929562872-bb421503ef21?auto=format&fit=crop&q=80&w=2000";

// Default icons for pillars
const PILLAR_ICONS = [Users, Sparkles, Shield, Clock, Star, Globe];

export default function Home() {
  const [featuredTrips, setFeaturedTrips] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const tripsPerPage = 8;

  const scrollPrev = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };
  
  const scrollNext = () => {
    const maxPage = Math.ceil(featuredTrips.length / tripsPerPage) - 1;
    setCurrentPage(prev => Math.min(maxPage, prev + 1));
  };

  const [homeDestinations, setHomeDestinations] = useState([]);
  const [heroMedia, setHeroMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allTripDestinations, setAllTripDestinations] = useState([]);
  const [currentDestPage, setCurrentDestPage] = useState(0);
  const [pageData, setPageData] = useState(null);
  const destsPerPage = 8;
  const [settings, setSettings] = useState({
    hero_title: "Te ayudamos a crear auténticos Viajes de Ensueño",
    hero_subtitle: "En Viajes de Ensueño creamos experiencias personalizadas para nuestros clientes, ofreciendo los mejores servicios.",
    hero_cta_primary: "Planifica tu Viaje",
    hero_cta_secondary: "Conócenos",
    pillars_title: "¿Por qué elegirnos?",
    custom_trips_title: "Viajes a Medida",
    custom_trips_subtitle: "Ya no se viaja para conocer lugares sino para vivirlos. Déjanos conocerte y organizaremos tu viaje de ensueño.",
    custom_trips_button: "Diseña tu Viaje"
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Page Content
        const pageRes = await axios.get(`${API}/pages/home?t=${Date.now()}`);
        setPageData(pageRes.data);

        // Fetch settings (for SEO and general params)
        const settingsRes = await axios.get(`${API}/settings?t=${Date.now()}`);
        if (settingsRes.data) {
          setSettings(prev => ({ ...prev, ...settingsRes.data }));
        }

        // Fetch featured trips
        const tripsRes = await axios.get(`${API}/trips?featured=true`);
        setFeaturedTrips(tripsRes.data);

        // Fetch home destinations
        const destsRes = await axios.get(`${API}/destinations?on_home=true`);
        setHomeDestinations(destsRes.data);

        // Fetch all unique destinations from trips
        const allTripsRes = await axios.get(`${API}/trips`);
        const uniqueDests = [...new Set(allTripsRes.data.map(t => t.destination))].filter(Boolean).sort();
        setAllTripDestinations(uniqueDests);

        // Fetch hero media
        const mediaRes = await axios.get(`${API}/media`);
        const heroMediaFiles = mediaRes.data.filter(m => m.category === 'hero' && m.is_active);
        if (heroMediaFiles.length > 0) {
          setHeroMedia(heroMediaFiles[0]);
        }
      } catch (err) {
        console.error('❌ Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (pageData) {
      document.title = pageData.seo_title || "Viajes de Ensueño | Tu Agencia de Viajes Personalizados";
      const description = pageData.seo_description || "Te ayudamos a crear auténticos Viajes de Ensueño. Agencia de viajes boutique especializada en experiencias personalizadas y destinos exclusivos.";
      document.querySelector('meta[name="description"]')?.setAttribute("content", description);
    }
  }, [pageData]);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="absolute inset-0">
          {heroMedia?.file_type === 'video' ? (
            <video
              src={`${process.env.REACT_APP_BACKEND_URL}${heroMedia.url}`}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={heroMedia ? `${process.env.REACT_APP_BACKEND_URL}${heroMedia.url}` : FALLBACK_HERO_IMAGE}
              alt="Viaje de ensueño"
              className="w-full h-full object-cover"
            />
          )}
          <div className="hero-gradient absolute inset-0" />
        </div>

        <div className="hero-content">
          <div className="max-w-3xl">
            <h1 
              className="text-4xl sm:text-5xl lg:text-7xl font-serif text-white mb-6 
                        animate-fade-in-up tracking-tight leading-tight"
              data-testid="hero-title"
            >
              {settings.hero_title.split('Viajes de Ensueño').map((part, index, array) => (
                <span key={index}>
                  {part}
                  {index < array.length - 1 && (
                    <span className="text-blue-400 font-bold">Viajes de Ensueño</span>
                  )}
                </span>
              ))}
            </h1>
            
            <p 
              className="text-lg lg:text-xl text-white/80 mb-10 animate-fade-in-up animation-delay-200 max-w-2xl whitespace-pre-line"
              data-testid="hero-subtitle"
            >
              {settings.hero_subtitle}
            </p>

            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-400">
              <Link
                to="/contacto"
                data-testid="hero-cta-primary"
                className="btn-accent border-0 flex items-center gap-2"
              >
                {settings.hero_cta_primary || "Planifica tu Viaje"}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/viajes"
                className="btn-accent bg-blue-600 hover:bg-blue-700 border-0 flex items-center gap-2 transition-all"
              >
                Explorar Viajes
                <Plane className="w-4 h-4" />
              </Link>
              <Link
                to="/sobre-nosotros"
                data-testid="hero-cta-secondary"
                className="btn-outline-white"
              >
                {settings.hero_cta_secondary || "Conócenos"}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars Section */}
      <section className="section bg-white" data-testid="pillars-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="section-title">
              {pageData?.sections?.[0]?.title || settings.pillars_title}
            </h2>
            <p className="section-subtitle mx-auto">
              {pageData?.sections?.[0]?.content || "Nuestros pilares nos definen y garantizan la mejor experiencia de viaje"}
            </p>
          </div>

          <div className="grid-features">
            {(pageData?.sections?.length > 1 ? pageData.sections.slice(1, 7) : [1, 2, 3, 4, 5, 6]).map((item, index) => {
              const IconComponent = PILLAR_ICONS[index % PILLAR_ICONS.length];
              const title = item.title || settings[`pillar_${index + 1}_title`];
              const description = item.content || settings[`pillar_${index + 1}_description`];
              
              if (!title) return null;

              return (
                <div
                  key={index}
                  className="feature-card animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`pillar-${index}`}
                >
                  <div className="icon-wrapper">
                    <IconComponent className="w-8 h-8 text-secondary" />
                  </div>
                  <h3 className="text-xl font-serif font-semibold text-primary mb-3">
                    {title}
                  </h3>
                  <p className="text-muted-foreground whitespace-pre-line">
                    {description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Trips Section */}
      <section className="section bg-slate-100" data-testid="featured-trips-section">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <h2 className="section-title">Experiencias Destacadas</h2>
              <p className="section-subtitle">
                Seleccionadas meticulosamente por nuestros expertos para este trimestre
              </p>
            </div>
            <div className="flex items-center gap-4 mt-6 md:mt-0">
               {featuredTrips.length > tripsPerPage && (
                 <div className="flex items-center gap-3">
                   <span className="text-xs font-bold text-primary/50 uppercase tracking-widest mr-2">
                     Página {currentPage + 1} de {Math.ceil(featuredTrips.length / tripsPerPage)}
                   </span>
                   <button 
                     onClick={scrollPrev}
                     disabled={currentPage === 0}
                     className={`w-10 h-10 rounded-full border border-primary/20 flex items-center justify-center transition-all shadow-sm ${currentPage === 0 ? 'opacity-30 cursor-not-allowed bg-slate-50' : 'hover:bg-white bg-white/50'}`}
                   >
                     <ChevronLeft className="w-5 h-5 text-primary" />
                   </button>
                   <button 
                     onClick={scrollNext}
                     disabled={currentPage >= Math.ceil(featuredTrips.length / tripsPerPage) - 1}
                     className={`w-10 h-10 rounded-full border border-primary/20 flex items-center justify-center transition-all shadow-sm ${currentPage >= Math.ceil(featuredTrips.length / tripsPerPage) - 1 ? 'opacity-30 cursor-not-allowed bg-slate-50' : 'hover:bg-white bg-white/50'}`}
                   >
                     <ChevronRight className="w-5 h-5 text-primary" />
                   </button>
                 </div>
               )}
               <Link
                to="/viajes"
                className="btn-secondary inline-flex items-center gap-2"
              >
                Ver todos los viajes
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="grid-trips">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="trip-card skeleton h-[400px]" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredTrips
                .slice(currentPage * tripsPerPage, (currentPage + 1) * tripsPerPage)
                .map((trip, index) => (
                  <div key={trip.id} className="animate-fade-in-up" style={{ animationDelay: `${index * 100}ms` }}>
                    <Link
                      to={`/viaje/${trip.id}`}
                      className="trip-card group hover-lift h-full"
                    >
                      <div className="trip-card-image-wrapper relative">
                        {/* Etiqueta de Descuento */}
                        {trip.discount && (
                          <div className="absolute top-4 left-4 z-30 bg-accent text-white px-4 py-1.5 rounded-full font-bold text-sm shadow-xl flex items-center gap-2 animate-pulse border-2 border-white/20">
                            <Tag className="w-4 h-4" />
                            {trip.discount}
                          </div>
                        )}
                        <img
                          src={trip.image_url}
                          alt={trip.title}
                          className="relative z-10 w-full h-full object-contain bg-slate-200 group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="trip-card-overlay z-20" />
                      </div>
                      <div className="trip-card-content">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="px-2 py-0.5 bg-accent/20 text-accent text-[10px] uppercase font-bold tracking-tighter rounded-full">
                            {trip.category || "Viaje"}
                          </span>
                        </div>
                        <span className="flex items-center gap-1 text-secondary text-xs uppercase tracking-widest font-bold">
                          {trip.destination}
                        </span>
                        <h3 className="text-xl font-serif font-bold mt-2 mb-4 line-clamp-2 min-h-[3.5rem]">
                          {trip.title}
                        </h3>
                        <p className="text-white/60 text-xs mb-4 line-clamp-2 italic">
                          {trip.short_description || "Una experiencia única diseñada a medida para viajeros exigentes."}
                        </p>
                        <div className="mt-auto flex items-center justify-between border-t border-white/10 pt-4">
                          <div className="flex items-center gap-2 text-white/80 text-sm">
                            <Clock className="w-4 h-4 text-accent" />
                            {trip.duration}
                          </div>
                          <div className="text-right">
                            <span className="block text-xs text-white/60 uppercase tracking-tighter">Desde</span>
                            <span className="text-accent font-bold text-xl">
                              {trip.price.toLocaleString('es-ES')}€
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
            </div>
          )}
        </div>
      </section>

      {/* Business Services Section */}
      <section className="py-24 bg-white overflow-hidden relative">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2 relative">
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-blue-50 rounded-full mix-blend-multiply filter blur-2xl opacity-70 animate-blob"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/10 rounded-full mix-blend-multiply filter blur-2xl opacity-70 animate-blob animation-delay-2000"></div>
              <img 
                src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=1200" 
                alt="Servicios para empresas" 
                className="rounded-3xl shadow-2xl relative z-10 hover-lift transition-transform duration-500"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-xl z-20 hidden md:block border border-slate-100 animate-fade-in">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-100 p-3 rounded-full text-blue-600">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="block text-2xl font-bold text-slate-900">+50</span>
                    <span className="text-sm text-slate-500">Empresas confían</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="lg:w-1/2">
              <h2 className="text-blue-600 font-bold uppercase tracking-widest text-sm mb-4">Empresas & Colectivos</h2>
              <h3 className="text-4xl md:text-5xl font-serif text-slate-900 mb-8 leading-tight">
                Gestionamos la movilidad de vuestro éxito
              </h3>
              <p className="text-lg text-slate-600 mb-10 leading-relaxed">
                Desde viajes de negocios de alta dirección hasta la logística de grandes clubes deportivos. Nuestro departamento especializado ofrece soluciones integrales para empresas, asociaciones y todo tipo de organizaciones.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-8 mb-12">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center text-blue-600">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Viajes Business</h4>
                    <p className="text-sm text-slate-500">Ahorro de costes y gestión 24/7 sin preocupaciones.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center text-blue-600">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">MICE & Eventos</h4>
                    <p className="text-sm text-slate-500">Convenciones, incentivos y team building.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center text-blue-600">
                    <Trophy className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Clubes Deportivos</h4>
                    <p className="text-sm text-slate-500">Logística especializada para competiciones.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center text-blue-600">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Asociaciones</h4>
                    <p className="text-sm text-slate-500">Viajes grupales con atención personalizada.</p>
                  </div>
                </div>
              </div>
              
              <Link to="/servicios-empresas">
                <Button size="lg" className="rounded-full px-10 bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-200 group">
                  Saber más
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Trips CTA Section */}
      <section className="py-24 bg-gradient-to-br from-primary via-primary-600 to-secondary relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAyYy0yLjIxIDAtNCAxLjc5LTQgNHMxLjc5IDQgNCA0IDQtMS43OSA0LTQtMS43OS00LTQtNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white mb-6">
              {settings.custom_trips_title}
            </h2>
            <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed whitespace-pre-line">
              {settings.custom_trips_subtitle}
            </p>
            <Link
              to="/viajes/medida"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary font-semibold rounded-lg hover:bg-white/90 transition-all shadow-lg hover:shadow-xl hover:scale-105"
            >
              {settings.custom_trips_button}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Recommended Destinations */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-4xl md:text-5xl font-serif text-primary mb-4">
                Destinos <span className="text-accent italic">que inspiran</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                Descubre guías profundas de los lugares más deseados del 2026 y tesoros ocultos que te sorprenderán.
              </p>
            </div>
            <div className="flex items-center gap-4">
              {homeDestinations.length > destsPerPage && (
                <div className="flex gap-2">
                  <button 
                    onClick={() => setCurrentDestPage(prev => Math.max(0, prev - 1))}
                    disabled={currentDestPage === 0}
                    className="p-2 rounded-full border border-slate-200 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Anterior"
                  >
                    <ChevronLeft className="w-5 h-5 text-primary" />
                  </button>
                  <button 
                    onClick={() => setCurrentDestPage(prev => ((prev + 1) * destsPerPage < homeDestinations.length ? prev + 1 : prev))}
                    disabled={(currentDestPage + 1) * destsPerPage >= homeDestinations.length}
                    className="p-2 rounded-full border border-slate-200 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Siguiente"
                  >
                    <ChevronRight className="w-5 h-5 text-primary" />
                  </button>
                </div>
              )}
              <div className="hidden sm:flex gap-2">
                <div className="px-4 py-2 bg-slate-100 rounded-full border border-slate-200 text-xs font-bold text-slate-400 uppercase tracking-widest">Trending</div>
                <div className="px-4 py-2 bg-accent/10 rounded-full border border-accent/20 text-xs font-bold text-accent uppercase tracking-widest">Secretos</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 min-h-[600px]">
            {(homeDestinations.length > 0 ? homeDestinations : [
              { name: 'Japón', slug: 'japon', image_url: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800' },
              { name: 'Islandia', slug: 'islandia', image_url: 'https://images.unsplash.com/photo-1504829857797-ddff29c27927?w=800' },
              { name: 'Uzbekistán', slug: 'uzbekistan', image_url: 'https://images.unsplash.com/photo-1528644489117-699112974b93?w=800' },
              { name: 'Bali', slug: 'bali', image_url: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800' },
              { name: 'Bután', slug: 'butan', image_url: 'https://images.unsplash.com/photo-1578516123447-509d435165d7?w=800' },
              { name: 'Albania', slug: 'albania', image_url: 'https://images.unsplash.com/photo-1590001158193-7903d2e5eec1?w=800' },
              { name: 'Georgia', slug: 'georgia', image_url: 'https://images.unsplash.com/photo-1565462016462-8e6f1ccf1f45?w=800' },
              { name: 'Omán', slug: 'oman', image_url: 'https://images.unsplash.com/photo-1578922863963-f09419be1900?w=800' },
            ]).slice(currentDestPage * destsPerPage, (currentDestPage + 1) * destsPerPage).map((dest, idx) => (
              <Link 
                key={dest.slug || dest.name} 
                to={`/destinos/${dest.slug}`}
                className="group relative h-80 rounded-[2rem] overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500"
              >
                <img 
                  src={dest.image_url || dest.img} 
                  alt={dest.name} 
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-2xl font-serif text-white mb-2">{dest.name}</h3>
                  <div className="flex items-center gap-2 text-accent text-sm font-bold opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all">
                    Explorar Guía <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
                {idx > 3 && (
                  <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold text-white uppercase tracking-widest border border-white/30">
                    Gema Oculta
                  </div>
                )}
              </Link>
            ))}

            {/* "And many more" block REMOVED */}
          </div>
        </div>
      </section>

      {/* Viajes a Medida Section - MOVED TO DESTINATIONS PAGE */}

      {/* Trust & Stats Section */}
      <section className="py-20 bg-primary text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl -ml-48 -mb-48" />
        <div className="container-custom relative z-10">
          <div className="grid md:grid-cols-4 gap-12 text-center">
            <div>
              <div className="text-4xl lg:text-5xl font-serif font-bold text-accent mb-2">+15</div>
              <p className="text-white/70 uppercase tracking-widest text-sm">Años de Experiencia</p>
            </div>
            <div>
              <div className="text-4xl lg:text-5xl font-serif font-bold text-accent mb-2">100%</div>
              <p className="text-white/70 uppercase tracking-widest text-sm">Viajes Personalizados</p>
            </div>
            <div>
              <div className="text-4xl lg:text-5xl font-serif font-bold text-accent mb-2">24h</div>
              <p className="text-white/70 uppercase tracking-widest text-sm">Asistencia en Destino</p>
            </div>
            <div>
              <div className="text-4xl lg:text-5xl font-serif font-bold text-accent mb-2">9.8/10</div>
              <p className="text-white/70 uppercase tracking-widest text-sm">Satisfacción Media</p>
            </div>
          </div>
          <div className="mt-20 flex flex-wrap justify-center items-center gap-12 opacity-50 grayscale hover:grayscale-0 transition-all duration-700">
             <div className="flex items-center gap-2">
               <Shield className="w-8 h-8" />
               <span className="font-bold tracking-tighter uppercase">Agencia Registrada</span>
             </div>
             <div className="flex items-center gap-2">
               <Globe className="w-8 h-8" />
               <span className="font-bold tracking-tighter uppercase">Viajes Sostenibles</span>
             </div>
             <div className="flex items-center gap-2">
               <Star className="w-8 h-8" />
               <span className="font-bold tracking-tighter uppercase">Calidad Turística</span>
             </div>
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="section bg-white" data-testid="instagram-section">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="section-title">Únete a nuestra comunidad</h2>
            <p className="section-subtitle mx-auto">
              Síguenos en Instagram y descubre inspiración para tu próximo viaje
            </p>
          </div>
          <InstagramFeed />
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />
    </Layout>
  );
}
